import type { PrescribableMedication } from './types';

/** The subset of a query's state the derivation reads — kept structural so the pure functions are
 * testable without react-query. */
export interface FormulationSourceState {
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
}

/** Whether a formulation carries a code `$order-medication` can be ordered by. RxNorm covers drugs;
 * devices and supplies are NDC-coded only, and the order request takes either — so testing RxNorm
 * alone would class a dispensable supply as free text and hide the prescribe/cart actions. */
export function isDispensable(formulation: PrescribableMedication): boolean {
  return Boolean(formulation.rxNorm) || Boolean(formulation.ndc);
}

/** Whether a formulation is a device or supply rather than a drug: NDC-coded with no RxNorm and no
 * vendor sig. There is nothing to derive dose/units/route from for these — ScriptSure sends no sig
 * or dose form, and no terminology resolves a dose form from an NDC (Medplum hosts no NDC code
 * system, and RxNorm does not carry the code) — so the form seeds supply defaults instead. */
export function isSupply(formulation: PrescribableMedication): boolean {
  return !formulation.rxNorm && Boolean(formulation.ndc) && !formulation.sigs?.length;
}

/** Words in a supply's catalog name or generic name that mean it ships as a single durable item
 * rather than a box of consumables. */
const DEVICE_KEYWORDS = ['meter', 'monitor', 'reader', 'receiver', 'transmitter', 'device', 'system', 'kit'];

/**
 * The container a supply is dispensed in, for the Units field.
 *
 * Consumables (test strips, lancets, pen needles, syringes, sensors) come by the box; durable items
 * (meters, readers, receivers, transmitters, monitors) come as one device. Matched on the vendor's
 * own naming — the generic name is the more reliable signal ("blood-glucose meter", "lancets",
 * "blood sugar diagnostic"), with the product name behind it.
 *
 * A guess either way, which is why it is only ever seeded into an empty field and every other unit
 * stays selectable.
 */
export function supplyUnit(formulation: PrescribableMedication, genericName?: string): string {
  const haystack = `${genericName ?? ''} ${formulation.name}`.toLowerCase();
  return DEVICE_KEYWORDS.some((keyword) => haystack.includes(keyword)) ? 'device' : 'box';
}

/** The value a formulation is selected by. RxNorm where there is one, else the NDC — supplies are
 * NDC-coded only, and a row keyed on an absent RxNorm renders unselectable. */
export function formulationKey(formulation: PrescribableMedication): string {
  return formulation.rxNorm ?? formulation.ndc ?? '';
}

export interface FormulationSearchResult {
  /** The strength/form options to offer for the chosen drug: ScriptSure's formulations when it has
   * any, else the RxNorm-catalog fallback rows. Every row carries an orderable code — RxNorm, or an
   * NDC for supplies. */
  formulations: PrescribableMedication[];
  /** True while either lookup is in flight — the picker shows its loading state. */
  isLoading: boolean;
  /** True once the lookups have settled: ScriptSure returned formulations, or the fallback finished
   * (with rows, empty, or in error). Settled with zero formulations is the manual-entry condition. */
  settled: boolean;
}

/** True once a drug is chosen but every formulation source has settled with no dispensable
 * formulation — ScriptSure's lookup AND the RxNorm-catalog fallback — the manual-entry condition. */
export function isManualEntry(hasDrug: boolean, formulationsLoaded: boolean, formulationCount: number): boolean {
  return hasDrug && formulationsLoaded && formulationCount === 0;
}

/** True once ScriptSure can no longer produce formulations for the chosen drug — it settled empty,
 * or the lookup itself failed. Either way the RxNorm-catalog fallback is the next step: on a vendor
 * outage the prescriber still gets prescribable strengths rather than a picker stuck in limbo. */
export function scriptSureExhausted(scriptSure: FormulationSourceState, formulationCount: number): boolean {
  return scriptSure.isError || (scriptSure.isSuccess && formulationCount === 0);
}

/** Derive the picker-facing result from the two sources. Pure so the settled/loading edge cases
 * (vendor failure, fallback transport error) are unit-testable without react-query.
 *
 * `settled` must only flip true on terminal states: ScriptSure succeeded with formulations, or the
 * fallback finished. A fallback *error* is terminal-with-nothing — the service already degrades
 * RxNav failures to `[]`, so an error here is a transport failure — and counts as settled so the
 * picker degrades to manual entry rather than sitting forever on an enabled-but-empty select.
 * A ScriptSure failure alone is NOT settled: the fallback fires next (see
 * {@link scriptSureExhausted}), and flipping settled early would flash manual entry while it loads. */
export function deriveFormulationSearch(args: {
  scriptSure: FormulationSourceState;
  fallback: FormulationSourceState;
  scriptSureFormulations: PrescribableMedication[];
  fallbackFormulations: PrescribableMedication[];
}): FormulationSearchResult {
  const { scriptSure, fallback, scriptSureFormulations, fallbackFormulations } = args;
  const fallbackSettled = fallback.isSuccess || fallback.isError;
  return {
    formulations: scriptSureFormulations.length > 0 ? scriptSureFormulations : fallbackFormulations,
    isLoading: scriptSure.isFetching || fallback.isFetching,
    settled: (scriptSure.isSuccess && scriptSureFormulations.length > 0) || fallbackSettled,
  };
}
